import moment from 'moment-timezone';
import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { DateRangePicker, DateType } from 'ui/datePicker';
import { ALL_TIME, RELATIVE_DATE_PRESETS_WITH_ALL_TIME } from 'ui/datePicker/helpers';
import { useClickAway } from 'react-use';
import { guessCurrentTimeZone } from 'utils/timeZone';
import { createQueryString } from '../utils';
import { fetchCustomEventsRequest } from '../actions';
import { CustomEventsDateTrigger } from './CustomEventsDateTrigger';
import { DatePickerWrapper, DatePickerBackground } from './CustomEventsDatePicker.styled';

type Props = {
  start: string;
  end: string;
  preset: string;
  setStartDate: (start: string) => void;
  setEndDate: (end: string) => void;
  setPreset: (preset: string) => void;
};

export const CustomEventsDatePicker = ({
  start,
  end,
  preset,
  setStartDate,
  setEndDate,
  setPreset,
}: Props) => {
  const [display, setDisplay] = useState({
    start,
    end,
    preset,
    timezone: guessCurrentTimeZone(),
    show: false,
  });

  const wrapperRef = useRef(null);
  const dispatch = useDispatch();

  const handleSetDisplay = (updates: any) => setDisplay((prev) => ({ ...prev, ...updates }));

  const handleSubmit = () => {
    const isAllTime = display.preset === ALL_TIME;
    setStartDate(isAllTime ? '' : display.start);
    setEndDate(isAllTime ? '' : display.end);
    setPreset(display.preset);

    const queryString = createQueryString(display.preset, display.start, display.end);
    dispatch(fetchCustomEventsRequest(queryString, dispatch));
    handleSetDisplay({ show: false });
  };

  const handleCancel = () => handleSetDisplay({ start, end, preset, show: false });

  const handlePresetChange = (newPreset: string) => {
    handleSetDisplay({ preset: newPreset });

    if (newPreset === ALL_TIME) {
      handleSetDisplay({ start: '', end: '' });
    }
  };

  const handleDateRangeChange = ({ startDate, endDate }: Record<string, DateType>) => {
    if (startDate && endDate) {
      handleSetDisplay({
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      });
    }
  };

  useClickAway(wrapperRef, handleCancel);

  return (
    <DatePickerWrapper ref={wrapperRef}>
      <CustomEventsDateTrigger
        onClick={() => handleSetDisplay({ show: !display.show })}
        preset={preset}
        start={start}
        end={end}
      />
      {display.show && (
        <DatePickerBackground>
          <DateRangePicker
            blockFutureDates
            dateRangePreset={display.preset}
            defaultTimeZone={display.timezone}
            endDate={display.end ? moment(display.end) : null}
            hasActions
            hasAllTimePreset
            onCancel={handleCancel}
            onDateRangeChange={handleDateRangeChange}
            onPresetChange={handlePresetChange}
            onSubmit={handleSubmit}
            onTimeZoneChange={(newTimezone) => handleSetDisplay({ timezone: newTimezone })}
            presets={RELATIVE_DATE_PRESETS_WITH_ALL_TIME}
            promoteCustomInput
            showRelativeDatePresets
            startDate={display.start ? moment(display.start) : null}
          />
        </DatePickerBackground>
      )}
    </DatePickerWrapper>
  );
};
